import { base44 } from "@/api/base44Client";
import {
  applyLocalCartChange,
  syncOfflineCartToServer,
  pushOfflineCartDirtyOnly,
  normalizeStoreId,
  normalizeBranchId,
} from "@/lib/offlineCart";
import { shouldPreferLocalData } from "@/lib/connectionQuality";

/** תלויות API לסנכרון סל — מרוכז במקום אחד */
export function getCartSyncDeps(storeId, branchId = "") {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  return {
    branchId: bid,
    updateCartItem: (payload) =>
      base44.entities.CartItem.update({
        ...payload,
        ...(bid ? { branch_id: bid } : { branch_id: payload.branch_id }),
      }),
    deleteCartItem: (id, meta) => base44.entities.CartItem.delete(id, meta),
    fetchList: () =>
      base44.entities.CartItem.list({
        store_id: sid,
        ...(bid ? { branch_id: bid } : {}),
      }),
  };
}

/**
 * עדכון מיידי ב-localStorage + דחיפה לשרת ברקע (לא חוסם UI).
 * @returns {Array} רשימת הסל המעודכנת
 */
export function pushCartItemChange(storeId, item, branchId = "") {
  const bid = normalizeBranchId(branchId ?? item?.branch_id);
  return applyLocalCartChange(
    storeId,
    { ...item, branch_id: bid },
    getCartSyncDeps(storeId, bid)
  );
}

/** סנכרון מלא של הסל המקומי לשרת (בפתיחת אפליקציה / חזרה לאינטרנט) */
export function runFullCartSync(storeId, queryClient, branchId = "") {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return Promise.resolve();
  return syncOfflineCartToServer(sid, {
    queryClient,
    ...getCartSyncDeps(sid, bid),
    branchId: bid,
  });
}

/**
 * ברשת חלשה: מעלה dirty בלבד (בלי משיכת סל כבדה).
 * תמיד בטוח לקרוא — לא מוחק פריטים מהסל.
 */
export function runCartDirtyPush(storeId, queryClient, branchId = "") {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return Promise.resolve();
  return pushOfflineCartDirtyOnly(sid, {
    queryClient,
    ...getCartSyncDeps(sid, bid),
    branchId: bid,
  });
}

/** בוחר סנכרון מלא או דחיפת dirty לפי איכות הרשת */
export function runSmartCartSync(storeId, queryClient, branchId = "") {
  if (shouldPreferLocalData()) {
    return runCartDirtyPush(storeId, queryClient, branchId);
  }
  return runFullCartSync(storeId, queryClient, branchId);
}
